import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calendar, Gift, TrendingUp } from "lucide-react";
import { CalculatorContent } from "./types";

interface CalculatorInputPanelProps {
  birthYear: number;
  giftValue: number;
  investmentPercentage: number;
  onBirthYearChange: (value: number) => void;
  onGiftValueChange: (value: number) => void;
  onInvestmentPercentageChange: (value: number) => void;
  content: CalculatorContent;
}

const CalculatorInputPanel = ({
  birthYear,
  giftValue,
  investmentPercentage,
  onBirthYearChange,
  onGiftValueChange,
  onInvestmentPercentageChange,
  content
}: CalculatorInputPanelProps) => {
  return (
    <Card className="border-2 border-legatia-green/20 shadow-lg">
      <CardContent className="p-6 space-y-8">
        {/* Birth Year Slider */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-legatia-green" />
            <Label className="text-sm font-semibold text-legatia-green">{content.bornIn}</Label>
          </div>
          <div className="space-y-2">
            <div className="text-2xl font-bold text-gray-900">{birthYear}</div>
            <Slider
              value={[birthYear]}
              onValueChange={(values) => onBirthYearChange(values[0])}
              min={1990}
              max={2020}
              step={1}
              className="w-full"
              aria-label={`${content.bornIn}: ${birthYear}`}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>1990</span>
              <span>2020</span>
            </div>
          </div>
        </div>

        {/* Gift Value Slider */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-legatia-green" />
            <Label className="text-sm font-semibold text-legatia-green">{content.giftsReceived}</Label>
          </div>
          <div className="space-y-2">
            <div className="text-2xl font-bold text-gray-900">€{giftValue.toLocaleString()}</div>
            <Slider
              value={[giftValue]}
              onValueChange={(values) => onGiftValueChange(values[0])}
              min={0}
              max={20000}
              step={100}
              className="w-full"
              aria-label={`${content.giftsReceived}: €${giftValue.toLocaleString()}`}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>€0</span>
              <span>€20,000</span>
            </div>
          </div>
        </div>

        {/* Investment Percentage Slider */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-legatia-green" />
            <Label className="text-sm font-semibold text-legatia-green">{content.investmentPart}</Label>
          </div>
          <div className="space-y-2">
            <div className="text-2xl font-bold text-gray-900">{investmentPercentage}%</div>
            <Slider
              value={[investmentPercentage]}
              onValueChange={(values) => onInvestmentPercentageChange(values[0])}
              min={0}
              max={100}
              step={5}
              className="w-full"
              aria-label={`${content.investmentPart}: ${investmentPercentage}%`}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>0%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CalculatorInputPanel;
